import { useEffect, useState } from "react"
import { useIntl } from "react-intl"

import classNames from "classnames"
import dayjs, { Dayjs } from "dayjs"

import { Dropdown, StaffBodyTextXS } from "@doktor-se/bones-ui"

import { getPostponeTimes } from "api"
import { useAppDispatch } from "lib/hooks"

import styles from "./PostponeTimes.module.scss"

const FULL_DATE_FORMAT = "DD-MM-YYYY"
const TIME_FORMAT = "HH:mm"

export interface PostponeTimesProps {
  conversationId: string
  setDate: (date: Dayjs) => any
  nowSelected: boolean
  setNowSelected: (nowSelected: boolean) => any
  selectedQueue: string | undefined
}

type GroupedPostponeTimes = {
  [date: string]: Dayjs[]
}

const PostponeTimes = ({ conversationId, setDate, nowSelected, setNowSelected, selectedQueue }: PostponeTimesProps) => {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const [groupedDates, setGroupedDates] = useState<GroupedPostponeTimes>({})
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs())
  const [selectedDay, setSelectedDay] = useState<string>()

  const groupedDate = Object.entries(groupedDates).find(([_, times]) =>
    times.some(time => time.format(FULL_DATE_FORMAT) === selectedDay)
  )

  const setSelectedTime = (date: Dayjs) => {
    setSelectedDate(date)
    setDate(date)
  }

  const groupDates = (postponeTimes: string[]): GroupedPostponeTimes => {
    return postponeTimes?.reduce((result: GroupedPostponeTimes, dateWithTime) => {
      const date = dayjs(dateWithTime)
      const dateStr = date.format(FULL_DATE_FORMAT)
      if (result[dateStr]) {
        result[dateStr].push(date)
      } else {
        result[dateStr] = [date]
      }
      return result
    }, {})
  }

  useEffect(() => {
    const loadPostponeTimes = async () => {
      if (!selectedQueue) return

      const response = await dispatch(getPostponeTimes(conversationId, selectedQueue))
      if (response) {
        const dates = groupDates(response.times)
        setGroupedDates(dates)
      }
    }
    loadPostponeTimes().then(() => {})
  }, [selectedQueue, conversationId, dispatch])

  return (
    <>
      <div className={styles.dateSelector}>
        <Dropdown
          fullWidth
          variant={"onSurface"}
          items={Object.keys(groupedDates).map(item => ({ id: item, name: item }))}
          label={intl.formatMessage({ id: "conversation.postpone.dates.label" })}
          selectedKey={nowSelected ? "{}" : selectedDay}
          onSelectionChange={item => {
            setSelectedDay(item.toString())
            setNowSelected(false)
          }}
          placeholder={intl.formatMessage({ id: "conversation.postpone.dropdown.placeholder" })}
        />
      </div>

      {groupedDate && (
        <div key={groupedDate[0]} className={styles.dateList}>
          {groupedDate[1].map(
            dateWithTime =>
              dateWithTime.format(FULL_DATE_FORMAT) === selectedDay && (
                <button
                  key={dateWithTime.toString()}
                  className={classNames(styles.timeChip, {
                    [styles.timeChipSelected]: !nowSelected && selectedDate === dateWithTime
                  })}
                  onClick={() => {
                    setSelectedTime(dateWithTime)
                    setNowSelected(false)
                  }}
                  color={"primary"}
                  id={dateWithTime.toISOString()}>
                  <StaffBodyTextXS>{dateWithTime.format(TIME_FORMAT)}</StaffBodyTextXS>
                </button>
              )
          )}
        </div>
      )}
    </>
  )
}
export default PostponeTimes
